import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useCurrentUser } from '../../Session';
import { useGetSaleTransactionsQuery, UserType, SaleTransactionStage, DownloadFileType } from '../../api';
import ListPagination from '../../components/ListPagination';
import { useLoading } from '../../components/LoadingProvider';
import { LocationHistory } from '../../components/LocationHistory';
import {
    SaleTransactionsHeader,
    SaleTransactionsBody,
    SaleTransactionsFooter,
    FilterDialog,
    DownloadSaleListFn,
} from './components';
import FilterMenu from './components/FilterMenu';

type ListContext = {
    search: string;
    page: number;
};

export type SaleTransactionsProps = {
    downloadSalesTransactionsFile: DownloadSaleListFn;
};

const pageSize = 9;

export type TransactionFilter = {
    fromCreated: Date | null;
    toCreated: Date | null;
    fromHandover: Date | null;
    toHandover: Date | null;
    stage: 'all' | SaleTransactionStage;
    awardedDealer: string;
    showArchived: boolean;
};

export const defaultTransactionFilter: TransactionFilter = {
    stage: 'all',
    fromCreated: null,
    toCreated: null,
    fromHandover: null,
    toHandover: null,
    awardedDealer: 'all',
    showArchived: false,
};

export type PurchaseOrSalesTransactionTabType = 'purchaseTransactions' | 'salesTransactions';

const SaleTransactions = ({ downloadSalesTransactionsFile }: SaleTransactionsProps) => {
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const [showFilterDialog, setShowFilterDialog] = useState(false);
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const currentUser = useCurrentUser();
    const { resetSalesTransactionsFilter = true } = (useLocation().state || {}) as LocationHistory;

    const defaultTransactionFilterLS = useMemo(
        () =>
            !resetSalesTransactionsFilter
                ? (JSON.parse(localStorage.getItem(`SaleTransactionsListLS-${currentUser.id}`)) as TransactionFilter) ||
                  defaultTransactionFilter
                : defaultTransactionFilter,
        [resetSalesTransactionsFilter, currentUser.id]
    );

    useEffect(() => {
        if (resetSalesTransactionsFilter) {
            localStorage.removeItem(`SaleTransactionsListLS-${currentUser.id}`);
        }
    }, [resetSalesTransactionsFilter, currentUser.id]);

    const [filter, setFilter] = useState<TransactionFilter>(defaultTransactionFilterLS);
    const setFilterAndPersist = useCallback(
        (data: TransactionFilter) => {
            setFilter(data);
            localStorage.setItem(`SaleTransactionsListLS-${currentUser.id}`, JSON.stringify(data));
        },
        [setFilter, currentUser.id]
    );

    const { attach } = useLoading();

    const [{ page, search }, setListContext] = useState<ListContext>({ search: '', page: 0 });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [page]);

    const queryFilter = useMemo(
        () => ({
            stages: filter.stage === 'all' ? null : [filter.stage],
            fromCreated: filter.fromCreated,
            toCreated: filter.toCreated,
            fromHandover: filter.fromHandover,
            toHandover: filter.toHandover,
            awardedDealer: filter.awardedDealer === 'all' ? null : filter.awardedDealer,
            showArchived: filter.showArchived,
        }),
        [filter]
    );

    const variables = useMemo(
        () => ({
            page: { limit: pageSize, offset: page * pageSize },
            filter: {
                search,
                ...queryFilter,
            },
            sort: {
                stagePriority: currentUser.type === UserType.Approver ? SaleTransactionStage.PendingApproval : null,
            },
        }),
        [page, search, queryFilter, currentUser.type]
    );

    const { data, refetch } = useGetSaleTransactionsQuery({
        fetchPolicy: 'cache-and-network',
        variables,
    });

    const refetchList = useCallback(() => refetch(variables), [refetch, variables]);

    const setValues = useCallback(
        (filter: TransactionFilter) => {
            setFilterAndPersist({
                ...filter,
            });
        },
        [setFilterAndPersist]
    );

    const setActivePage = useCallback(
        (newPage: number) => {
            setListContext(state => ({ ...state, page: newPage }));
        },
        [setListContext]
    );

    const searchOnChange = useCallback(
        (searchValue: string) => {
            setListContext(state => ({ ...state, search: searchValue }));
        },
        [setListContext]
    );

    const count = data?.saleTransactions?.count || 0;
    const saleTransactions = data?.saleTransactions?.items || [];

    const downloadFile = useCallback(
        (type: DownloadFileType) => {
            const execute = async () => {
                await downloadSalesTransactionsFile({
                    downloadFileType: type,
                    filter: {
                        search,
                        ...queryFilter,
                    },
                    sort: {
                        stagePriority:
                            currentUser.type === UserType.Approver ? SaleTransactionStage.PendingApproval : null,
                    },
                });
            };

            attach(execute());
        },
        [attach, currentUser.type, downloadSalesTransactionsFile, queryFilter, search]
    );

    return (
        <>
            <FilterDialog filter={filter} open={showFilterDialog} setOpen={setShowFilterDialog} setValues={setValues} />
            <FilterMenu filter={filter} open={showFilterMenu} setOpen={setShowFilterMenu} setValues={setValues} />
            <SaleTransactionsHeader
                downloadExcel={() => downloadFile(DownloadFileType.Excel)}
                downloadPdf={() => downloadFile(DownloadFileType.Pdf)}
                refreshFn={refetchList}
                search={search}
                searchOnChange={searchOnChange}
                setShowFilterDialog={setShowFilterDialog}
                setShowFilterMenu={setShowFilterMenu}
            />
            <SaleTransactionsBody saleTransactions={saleTransactions} hideAwardedBitAmount />
            <ListPagination activePage={page + 1} count={count} pageSize={pageSize} setActivePage={setActivePage} />
            {isSmall && <SaleTransactionsFooter />}
        </>
    );
};

export default SaleTransactions;
